/* eslint-disable @typescript-eslint/no-unused-vars */
import { container } from 'tsyringe';
import { ApiClient } from './api-client';
/**
 * The abstract Crud Service.
 */
export class CrudService {
    constructor() {
        this.client = container.resolve(ApiClient);
        /**
         * Alias for findOne method, retrieves an item by ID.
         *
         * @param {NumberOrString} [id] - The ID of the item to retrieve.
         * @param {RequestQuery} [query] - Optional query parameters for the retrieval request.
         * @returns {Promise<SimpleResponse<R>>} A Promise that resolves to a SimpleResponse containing the retrieved item.
         */
        this.getOne = this.findOne;
        /**
         * Alias for the find method, retrieves multiple items based on optional query parameters.
         *
         * @param {RequestQuery} [query] - Optional query parameters for the retrieval request.
         * @returns {Promise<CollectionResponse<R>>} A Promise that resolves to a CollectionResponse containing the retrieved items.
         */
        this.getAll = this.find;
    }
    /**
     * Creates one or more items.
     *
     * @param {C} item - The item or items to create.
     * @returns {Promise<SimpleResponse<C>>} A Promise that resolves to a SimpleResponse containing the created item(s).
     */
    create(item) {
        return this.client.post(this.route, item);
    }
    /**
     * Updates an existing item.
     *
     * @param {U} data - The data to update the item with.
     * @param {NumberOrString} [id] - The ID of the item to update.
     * @param {RequestQuery} [query] - Optional query parameters for the update request.
     * @returns {Promise<SimpleResponse<U>>} A Promise that resolves to a SimpleResponse containing the updated item.
     */
    update(data, id, query) {
        return this.client.patch(this.route, data, id, query);
    }
    /**
     * Updates an existing item.
     *
     * @param {U} data - The data to update the item with.
     * @param {NumberOrString} [id] - The ID of the item to update.
     * @param {RequestQuery} [query] - Optional query parameters for the update request.
     * @returns {Promise<SimpleResponse<U>>} A Promise that resolves to a SimpleResponse containing the updated item.
     */
    updatePut(data, id, query) {
        return this.client.put(this.route, data, id, query);
    }
    /**
     * Retrieves an item by its unique ID.
     *
     * @param {NumberOrString} [id] - The ID of the item to retrieve.
     * @param {RequestQuery} [query] - Optional query parameters for the retrieval request.
     * @param {RequestInit} [init] - Optional request initialization parameters.
     * @returns {Promise<SimpleResponse<R>>} A Promise that resolves to a SimpleResponse containing the retrieved item.
     */
    findOne(id, query, init) {
        return this.client.get(this.route, id, query, init);
    }
    /**
     * Deletes an item by its ID.
     *
     * @param {NumberOrString} id - The ID of the item to delete.
     * @returns {Promise<SimpleResponse<D>>} A Promise that resolves to a SimpleResponse indicating the deletion status.
     */
    delete(id) {
        return this.client.delete(this.route, id);
    }
    /**
     * Deletes an item by its ID.
     *
     * @param {NumberOrString} sku - The ID of the item to delete.
     * @param {boolean} useLoyaltyPoints - Whether to use loyalty points for the deletion.
     * @returns {Promise<SimpleResponse<D>>} A Promise that resolves to a SimpleResponse indicating the deletion status.
     */
    deleteWithBody(sku, useLoyaltyPoints) {
        return this.client.deleteWithBody(this.route, { sku, useLoyaltyPoints });
    }
    /**
     * Retrieves multiple items based on optional query parameters.
     *
     * @param {RequestQuery} [query] - Optional query parameters for the retrieval request.
     * @param {RequestInit} [init] - Optional request initialization parameters.
     * @returns {Promise<CollectionResponse<R>>} A Promise that resolves to a CollectionResponse containing the retrieved items.
     */
    find(query, init) {
        return this.client.get(this.route, undefined, query, init);
    }
    /**
     * Defines if the current Service supports batch operations.
     */
    get hasBatchSupport() {
        return !!this.batchSupport;
    }
    /**
     * Gets the platform information from the client.
     */
    get platform() {
        return this.client.platform;
    }
}
//# sourceMappingURL=crud.service.js.map